import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Inbox, Loader2, MessageCircle, Search, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { EmptyCard, ErrorCard, Field, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { useAdminSession } from "../components/jamora-admin/SessionProvider";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { DEMO_STATUS_LABELS, DEMO_STATUS_TONES, DEMO_TOPIC_LABELS, adminError, isSessionRequired, whatsappLink } from "../lib/jamoraAdmin";
import { patchedSearch } from "../lib/searchParams";
import { formatDateTime } from "../lib/tickets";

const STATUS_TABS = [["", "Toutes"], ...Object.entries(DEMO_STATUS_LABELS)];
const ALL_TOPICS = "__all__";

// A Dialog: a bottom sheet on a phone (rule § 23), a centred window on a computer.
const CONVERTIBLE_TOPICS = ["demo", "autre"];

function RequestSheet({ request, onClose, onConverted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState(null);
  useEffect(() => { setProblem(null); }, [request?.id]);
  const link = request ? whatsappLink(request.phone, request.pharmacy_name) : "";
  const canConvert = !!request && !request.converted && CONVERTIBLE_TOPICS.includes(request.topic) && hasPermission(user, "prospects:create");
  const convert = async () => {
    if (sending) return;
    setSending(true);
    setProblem(null);
    try {
      const { data } = await api.post(`/jamora-admin/demo-requests/${request.id}/convert`);
      toast.success("Prospect créé");
      onConverted(request.id, data.prospect_id);
      navigate(`/prospects/${data.prospect_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "already_converted") onConverted(request.id, detail.prospect_id);
      setProblem({ message: detail?.msg || detail?.message || "Impossible de créer le prospect. Réessayez dans quelques instants.", prospects: detail?.prospects || [] });
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={!!request} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="demo-sheet">
        {request && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-6 text-left">{request.pharmacy_name}</DialogTitle>
              <DialogDescription className="text-left">{DEMO_TOPIC_LABELS[request.topic] || request.topic} · {formatDateTime(request.created_at)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Pill tone={DEMO_STATUS_TONES[request.status] || "slate"}>{DEMO_STATUS_LABELS[request.status] || request.status}</Pill>
              <dl className="divide-y divide-slate-100">
                <Field label="E-mail"><a className="text-jamora hover:underline" href={`mailto:${request.email}`}>{request.email}</a></Field>
                <Field label="Téléphone">{request.phone || "—"}</Field>
                <Field label="Message"><span className="whitespace-pre-wrap" data-testid="demo-message">{request.message || "—"}</span></Field>
              </dl>
              {(link || canConvert) && (
                <div className="flex flex-wrap gap-2">
                  {link && (
                    <Button asChild variant="outline" size="sm"><a href={link} target="_blank" rel="noopener noreferrer" data-testid="demo-whatsapp"><MessageCircle size={14} className="mr-1.5" />Écrire sur WhatsApp</a></Button>
                  )}
                  {canConvert && (
                    <Button type="button" size="sm" onClick={convert} disabled={sending} data-testid="demo-convert">
                      {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UserPlus size={14} className="mr-1.5" />}Convertir en prospect
                    </Button>
                  )}
                </div>
              )}
              {request.converted && (
                <p className="text-sm text-emerald-800" data-testid="demo-converted">
                  Déjà devenue un prospect du CRM. <Link to={`/prospects/${request.converted.prospect_id}`} className="underline" data-testid="demo-converted-link">Ouvrir la fiche</Link>
                </p>
              )}
              {problem && (
                <div className="text-sm text-red-700 space-y-1" role="alert" data-testid="demo-convert-error">
                  <p>{problem.message}</p>
                  {problem.prospects.map((p) => <p key={p.id}><Link to={`/prospects/${p.id}`} className="underline">{p.pharmacy_name}</Link></p>)}
                </div>
              )}
              <p className="text-xs text-slate-500">Convertir crée le prospect dans le CRM seulement : le statut de la demande ne change pas dans Jamora (il se change dans l'administration Django de Jamora).</p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function JamoraAdminDemoRequests() {
  const session = useAdminSession();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const topic = params.get("topic") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [state, setState] = useState({ phase: "loading", items: [], cursor: null, error: "" });
  const [open, setOpen] = useState(null);
  const seq = useRef(0);

  const update = (changes) => setParams(patchedSearch(changes), { replace: true });
  useEffect(() => {
    const timer = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (cursor) => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: cursor ? "more" : "loading", error: "", items: cursor ? s.items : [] }));
    try {
      const { data } = await api.get("/jamora-admin/demo-requests", { params: { limit: 50, ...(search ? { search } : {}), ...(status ? { status } : {}), ...(topic ? { topic } : {}), ...(cursor ? { cursor } : {}) } });
      if (mine !== seq.current) return;
      setState((s) => ({ phase: "ready", items: cursor ? [...s.items, ...data.items] : data.items, cursor: data.next_cursor, error: "" }));
      session?.touch();
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { session?.expire(); return; }
      setState((s) => ({ ...s, phase: "error", error: adminError(e) }));
    }
  }, [search, status, topic]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(null); return () => { seq.current += 1; }; }, [load]);

  const filtered = !!(search || status || topic);
  const reset = () => { setSearchInput(""); setParams({}, { replace: true }); };

  return (
    <div className="space-y-4" data-testid="admin-demo-requests">
      <PageHeader title="Demandes de démo" description="Les demandes de contact reçues depuis le site de Jamora : démonstration, support, partenariat." />
      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Statut">
        {STATUS_TABS.map(([id, label]) => (
          <button key={id || "all"} type="button" role="tab" aria-selected={status === id} onClick={() => update({ status: id })} data-testid={`demo-tab-${id || "all"}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${status === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Pharmacie, e-mail, téléphone…" className="pl-9 h-9" aria-label="Rechercher une demande" data-testid="demo-search" />
        </div>
        <Select value={topic || ALL_TOPICS} onValueChange={(v) => update({ topic: v === ALL_TOPICS ? "" : v })}>
          <SelectTrigger className="h-9 w-48" aria-label="Sujet" data-testid="demo-topic"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TOPICS}>Tous les sujets</SelectItem>
            {Object.entries(DEMO_TOPIC_LABELS).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {state.phase === "loading" && <LoadingBlock rows={4} />}
      {state.phase === "error" && <ErrorCard message={state.error} onRetry={() => load(null)} />}
      {state.phase === "ready" && !state.items.length && (
        <EmptyCard icon={Inbox} testid="demo-empty" title={filtered ? "Aucune demande ne correspond" : "Aucune demande pour le moment"}
          hint={filtered ? "Essayez un autre filtre." : "Les demandes envoyées depuis le site de Jamora apparaîtront ici."}>
          {filtered && <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>}
        </EmptyCard>
      )}
      {(state.phase === "ready" || state.phase === "more") && state.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="demo-table">
            <Table>
              <TableHeader><TableRow><TableHead>Pharmacie</TableHead><TableHead>Contact</TableHead><TableHead>Sujet</TableHead><TableHead>Statut</TableHead><TableHead>Reçue le</TableHead></TableRow></TableHeader>
              <TableBody>
                {state.items.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setOpen(r)} data-testid={`demo-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-900">{r.pharmacy_name}{r.converted && <span className="ml-2 align-middle"><Pill tone="emerald">Prospect</Pill></span>}</TableCell>
                    <TableCell className="text-sm"><div>{r.email}</div><div className="text-xs text-slate-500">{r.phone || "—"}</div></TableCell>
                    <TableCell className="text-sm">{DEMO_TOPIC_LABELS[r.topic] || r.topic}</TableCell>
                    <TableCell><Pill tone={DEMO_STATUS_TONES[r.status] || "slate"}>{DEMO_STATUS_LABELS[r.status] || r.status}</Pill></TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{formatDateTime(r.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="demo-cards">
            {state.items.map((r) => (
              <button key={r.id} type="button" onClick={() => setOpen(r)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`demo-card-${r.id}`}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="font-medium text-slate-900">{r.pharmacy_name}{r.converted && <span className="ml-2 align-middle"><Pill tone="emerald">Prospect</Pill></span>}</div>
                  <div className="text-xs text-slate-500 break-all">{r.email}</div>
                  <div className="flex items-center gap-2"><Pill tone={DEMO_STATUS_TONES[r.status] || "slate"}>{DEMO_STATUS_LABELS[r.status] || r.status}</Pill><span className="text-xs text-slate-500">{formatDateTime(r.created_at)}</span></div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" aria-hidden="true" />
              </button>
            ))}
          </Card>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500" data-testid="demo-count">{state.items.length} demande{state.items.length > 1 ? "s" : ""} affichée{state.items.length > 1 ? "s" : ""}{state.cursor ? " (il y en a d'autres)" : ""}</p>
            {state.cursor && <Button variant="outline" size="sm" onClick={() => load(state.cursor)} disabled={state.phase === "more"} data-testid="demo-more">{state.phase === "more" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Voir plus</Button>}
          </div>
        </>
      )}
      <RequestSheet request={open} onClose={() => setOpen(null)} onConverted={(id, prospectId) => {
        const converted = { prospect_id: prospectId, at: new Date().toISOString() };
        setState((st) => ({ ...st, items: st.items.map((r) => (r.id === id ? { ...r, converted } : r)) }));
        setOpen((r) => (r && r.id === id ? { ...r, converted } : r));
      }} />
    </div>
  );
}
